import { useField } from "formik";
import { useState } from "react";
import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import SearchableSelect, { SearchableSelectOption } from "@/components/ui/SearchableSelect";

interface SearchableSelectFieldProps {
  name: string;
  label: string;
  options: SearchableSelectOption[];
  required?: boolean;
  description?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
  /** When true, shows a toggle to switch between picker and variable (template) input */
  variableToggle?: boolean;
}

export default function SearchableSelectField({
  name,
  label,
  options,
  required,
  description,
  placeholder,
  searchPlaceholder,
  width,
  variableToggle = false,
}: SearchableSelectFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const [isVariable, setIsVariable] = useState(
    () => typeof field.value === "string" && field.value.includes("{{")
  );

  return (
    <Box mb="4">
      <Flex justify="between" align="center" mb="1">
        <Text as="label" size="2" weight="medium">
          {label}
          {required && <Text color="red"> *</Text>}
        </Text>
        {variableToggle && (
          <Button
            type="button"
            variant="ghost"
            size="1"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsVariable(!isVariable); }}
            style={{ fontSize: "11px", padding: "2px 6px" }}
          >
            {isVariable ? "Use picker" : "Use variable"}
          </Button>
        )}
      </Flex>

      {isVariable ? (
        <TextField.Root
          value={field.value || ""}
          onChange={(e) => helpers.setValue(e.target.value)}
          placeholder="{{ $anythink.trigger.data.user_id }}"
          autoComplete="one-time-code"
        />
      ) : (
        <SearchableSelect
          value={field.value || ""}
          onChange={(v) => helpers.setValue(v)}
          options={options}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          width={width}
        />
      )}

      {description && (
        <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
          {description}
        </Text>
      )}

      {meta.touched && meta.error && (
        <Text size="1" color="red" mt="1" style={{ display: "block" }}>
          {meta.error}
        </Text>
      )}
    </Box>
  );
}
